import { ReactComponent as DownArrow } from 'atoms/icons/svg/DownArrow.svg';
import { ReactComponent as UpArrow } from 'atoms/icons/svg/UpArrow.svg';
import {
  Button,
  Collapse,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
} from '@mui/material';
import { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink as RouterLink, useNavigate } from 'react-router-dom';

interface SidebarMenuItemProps {
  children?: ReactNode;
  link?: string;
  icon?: ReactNode;
  badge?: string;
  open?: boolean;
  active?: boolean;
  onClick?: () => void;
  isExpanded?: boolean;
  className?: string;
  name:
    | 'Overview'
    | 'Statistics'
    | 'Departments'
    | 'Hole Detections'
    | 'Locations'
    | 'Pens'
    | 'Users'
    | 'Setting'
    | 'Customers'
    | 'AquaControl'
    | 'Notifications'
    | 'Sites'
    | 'Help'
    | 'Top boxes'
    | 'Log out'
    | 'Robots'
    | 'Auto Recordings'
    | 'Manual Recordings';
}

const SidebarMenuItem: FC<SidebarMenuItemProps> = ({
  children,
  link,
  icon: Icon,
  open: openParent,
  active,
  name,
  onClick,
  isExpanded,
  ...rest
}) => {
  const [menuToggle, setMenuToggle] = useState<boolean>(openParent ?? false);
  const { t } = useTranslation();
  const toggleMenu = (e): void => {
    e.preventDefault();
    setMenuToggle((Open) => !Open);
  };
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();
  const [openPopover, setOpenPopover] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState({
    left: '-500px',
    top: '-500px',
  });
  const handleMouseEnter = () => {
    if (name === 'Overview') {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      setOpenPopover(true);
    }
  };

  const handleMouseLeave = () => {
    if (name === 'Overview') {
      const id = setTimeout(() => {
        setOpenPopover(false);
      }, 100);

      setTimeoutId(id);
    }
  };

  const handleListItemClick = () => {
    if (onClick) {
      onClick();
      return;
    }

    if (link?.includes('http')) {
      window.open(link, '_blank');

      return;
    }

    if (!link) {
      console.error('Could not handle menu item click', {
        link,
        name,
      });
      return;
    }
    navigate(link);
  };

  useEffect(() => {
    if (active === false || isExpanded === false) {
      setMenuToggle(true);
    }
  }, [active, isExpanded]);

  useEffect(() => {
    const element = document.querySelector('a[href="/dashboard"]');

    if (element && openPopover) {
      const rect = element.getBoundingClientRect();

      setPopoverPosition({
        left: `${rect.right}px`,
        top: `${rect.top}px`,
      });
    }
  }, [openPopover]);

  const isCollapsible = !!children && isExpanded;

  const sideBarContent = useMemo(() => {
    if (isCollapsible) {
      return (
        <Stack
          display={'flex'}
          flexDirection={'column'}
          alignItems={'stretch'}
          width={'100%'}
        >
          <Button
            className={active ? 'selected-menu' : ''}
            component={onClick ? 'div' : RouterLink}
            to={link ?? ''}
            style={{
              justifyContent: 'start',
              padding: '12px',
            }}
            startIcon={Icon}
            endIcon={
              !menuToggle ? (
                <UpArrow onClick={toggleMenu} />
              ) : (
                <DownArrow onClick={toggleMenu} />
              )
            }
          >
            {isExpanded && t(name)}
          </Button>
          <Collapse className="menu-collapse" in={!menuToggle}>
            {children}
          </Collapse>
        </Stack>
      );
    }

    return (
      <ListItemButton
        className={active ? 'selected-menu' : ''}
        component={onClick ? 'div' : RouterLink}
        style={{ padding: '12px' }}
      >
        <Stack
          display={'flex'}
          gap="12px"
          flexDirection={'row'}
          alignItems={'center'}
        >
          {Icon && (
            <ListItemIcon
              className={
                isExpanded ? 'list-icon__collapsed' : 'list-icon__expanded'
              }
            >
              {Icon}
            </ListItemIcon>
          )}

          {isExpanded && (
            <ListItemText
              sx={{ fontSize: '14px', lineHeight: '24px', margin: 'unset' }}
              primary={t(name)}
              color="#00263A"
            />
          )}
        </Stack>
      </ListItemButton>
    );
  }, [isCollapsible, isExpanded, active, menuToggle, name, link]);

  return (
    <>
      <ListItem
        component="div"
        key={name}
        {...rest}
        onClick={handleListItemClick}
        {...(!isCollapsible && {
          onMouseEnter: handleMouseEnter,
          onMouseLeave: handleMouseLeave,
        })}
        sx={{
          padding: 0,
          marginBlock: 0,
          height: isCollapsible ? 'auto' : '48px',
          width: isExpanded ? '192px' : '48px',
          margin: '0 auto',
        }}
      >
        <Tooltip title={isExpanded || children ? '' : t(name)} placement="left">
          {sideBarContent}
        </Tooltip>
        {children && openPopover && (
          <div className="menu-popover" style={{ ...popoverPosition }}>
            <div className="menu-popover-paper">{children}</div>
          </div>
        )}
      </ListItem>
    </>
  );
};

export default SidebarMenuItem;
